import cx from 'classnames'
import { observer } from 'mobx-react-lite'

import IconButton from '@src/component/IconButton'
import ChipInput from '@src/component/chip-input'
import type { ChipInputProps } from '@src/component/chip-input'
import Typography from '@ui/Typography'
import { ClearTextIcon } from '@ui/icons/tint/16/general'
import { SearchIcon } from '@ui/icons/tint/20/general'

import type { IdentityRowInstance } from './PhoneNumberSelectorController'
import type PhoneNumberSelectorController from './PhoneNumberSelectorController'
import * as styles from './search-bar.css'

interface SearchBarProps {
  controller: PhoneNumberSelectorController
  renderChip?: ChipInputProps<IdentityRowInstance>['renderChip']
}

const SearchBar = ({ controller, renderChip }: SearchBarProps) => {
  const handleDelete = (index: number) => {
    const selection = controller.selections[index]
    if (!selection) {
      return
    }
    controller.clearSelection(selection)
  }

  return (
    <div
      className={cx(
        styles.root({
          size: controller.size,
          focused: controller.focused,
        }),
      )}
    >
      {controller.mode === 'single' ? (
        <>
          <Typography color="textSecondary" className={styles.icon}>
            <SearchIcon />
          </Typography>
          <input
            ref={controller.inputRef}
            autoFocus
            placeholder={controller.lang.PLACEHOLDER}
            data-allow-keyboard-when-empty
            value={controller.search}
            onChange={controller.handleSearchChange}
            onFocus={controller.handleSearchFocus}
            onBlur={controller.handleSearchBlur}
            className={styles.input({ size: controller.size })}
          />
          {controller.search ? (
            // eslint-disable-next-line custom-rules/no-deprecated-buttons, @typescript-eslint/no-deprecated -- FIXME: https://linear.app/openphone/issue/UXP-4347/migrate-deprecated-buttons-to-ds-button
            <IconButton
              tabIndex={-1}
              title="Clear search"
              icon={<ClearTextIcon />}
              size={22}
              onClick={controller.handleSearchClear}
              style={{ margin: '-1px 10px -1px 0' }}
            />
          ) : null}
        </>
      ) : (
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        <ChipInput<IdentityRowInstance>
          inputRef={controller.inputRef}
          size={controller.size}
          autoFocus
          placeholder={controller.lang.PLACEHOLDER}
          className={styles.chipInput({
            size: controller.size,
            hasSelections: controller.selections.length > 0,
          })}
          value={controller.search}
          chips={controller.selectedRows}
          onChange={controller.handleSearchChange}
          onDelete={handleDelete}
          onFocus={controller.handleSearchFocus}
          onBlur={controller.handleSearchBlur}
          renderChip={renderChip}
        />
      )}
    </div>
  )
}

export default observer(SearchBar)
